import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import Toast from '../../components/Toast';
import ConfirmModal from '../../components/ConfirmModal';
import CustomSelect from '../../components/common/CustomSelect';
import { organizationApi, adminApi } from '../../services/api';
import { getCountryFlag } from '../../utils/countryFlag';

export default function OrganizationDetail() {
  const { organizationId } = useParams();
  const navigate = useNavigate();
  const [organization, setOrganization] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ open: false, type: 'success', message: '' });
  const [showAddAdminModal, setShowAddAdminModal] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [adminToRemove, setAdminToRemove] = useState(null);

  const role = localStorage.getItem('role');
  const isGlobalAdmin = role === 'global_admin';
  const isOrgAdmin = role === 'admin';
  const canManageAdmins = isGlobalAdmin || isOrgAdmin;

  const showToast = (type, message) => {
    setToast({ open: true, type, message });
    setTimeout(() => setToast(prev => ({ ...prev, open: false })), 3000);
  };

  useEffect(() => {
    fetchOrganizationData();
  }, [organizationId]);

  const fetchOrganizationData = async () => {
    try {
      setLoading(true);
      const [orgRes, adminsRes] = await Promise.all([
        organizationApi.getById(organizationId),
        organizationApi.getAdmins(organizationId).catch(() => ({ data: [] })),
      ]);
      setOrganization(orgRes.data);
      setAdmins(adminsRes.data || []);
    } catch (error) {
      console.error('Error fetching organization:', error);
      showToast('error', 'Failed to load organization details');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableAdmins = async () => {
    try {
      const response = await adminApi.getAllUsers(0, 1000);
      const assignedIds = new Set(admins.map((a) => a.id));
      // Backend assign_organization_admin accepts only role 'admin' for now
      const available = (response.data || []).filter(
        (u) => u.role === 'admin' && !assignedIds.has(u.id)
      );
      setAllUsers(available);
    } catch (error) {
      console.error('Error fetching users:', error);
      showToast('error', 'Failed to load users');
    }
  };

  const handleAddAdmin = async (e) => {
    e.preventDefault();
    if (!selectedUserId) return;
    try {
      await organizationApi.assignAdmin(organizationId, selectedUserId);
      showToast('success', 'Admin assigned to organization successfully');
      setShowAddAdminModal(false);
      setSelectedUserId('');
      fetchOrganizationData();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to assign admin';
      showToast('error', msg);
    }
  };

  const handleRemoveAdmin = (admin) => {
    setAdminToRemove(admin);
    setConfirmOpen(true);
  };

  const confirmRemoveAdmin = async () => {
    if (!adminToRemove) return;
    try {
      await organizationApi.removeAdmin(organizationId, adminToRemove.id);
      showToast('success', 'Admin removed from organization successfully');
      fetchOrganizationData();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to remove admin';
      showToast('error', msg);
    } finally {
      setConfirmOpen(false);
      setAdminToRemove(null);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 py-8 flex justify-center items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-eucnet-blue"></div>
        </div>
      </Layout>
    );
  }

  if (!organization) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <h2 className="text-xl font-semibold text-eucnet-navy mb-2">Organization not found</h2>
            <p className="text-eucnet-grey-600 mb-4">The requested organization could not be found.</p>
            <button
              onClick={() => navigate('/admin/organizations')}
              className="bg-eucnet-blue hover:bg-eucnet-navy text-white px-4 py-2 rounded-lg"
            >
              Back to Organization Management
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>

      <Toast
        open={toast.open}
        setOpen={(open) => setToast(prev => ({ ...prev, open }))}
        type={toast.type}
        className="fixed top-6 right-6 z-[60]"
      >
        {toast.message}
      </Toast>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button
          onClick={() => navigate('/admin/organizations')}
          className="mb-4 text-eucnet-blue hover:text-eucnet-navy flex items-center"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
          </svg>
          Back to Organization Management
        </button>

        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex justify-between items-start mb-4">
            <div className="flex items-start gap-4">
              {organization.logo && (
                <img
                  src={organization.logo.startsWith('data:') ? organization.logo : `data:image/jpeg;base64,${organization.logo}`}
                  alt={`${organization.name} logo`}
                  className="h-16 w-auto max-w-full object-contain bg-gray-50 rounded border border-eucnet-grey-200"
                />
              )}
              <div>
                <h1 className="text-3xl font-bold text-eucnet-navy">{organization.name}</h1>
                {organization.short_name && (
                  <p className="mt-1 text-eucnet-grey-600">{organization.short_name}</p>
                )}
                {organization.description && (
                  <p className="mt-2 text-eucnet-grey-600">{organization.description}</p>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-6 pt-6 border-t border-eucnet-grey-200">
            {organization.country && (
              <div>
                <div className="text-sm text-eucnet-grey-600 mb-1">Country</div>
                <div className="text-lg font-semibold text-eucnet-navy flex items-center gap-2">
                  <span>{organization.country}</span>
                  <span className="text-3xl inline-block align-middle" aria-hidden="true">{getCountryFlag(organization.country)}</span>
                </div>
              </div>
            )}
            {organization.email && (
              <div>
                <div className="text-sm text-eucnet-grey-600 mb-1">Email</div>
                <div className="text-lg font-semibold text-eucnet-navy">
                  <a href={`mailto:${organization.email}`} className="text-eucnet-blue hover:underline">
                    {organization.email}
                  </a>
                </div>
              </div>
            )}
            {organization.website && (
              <div>
                <div className="text-sm text-eucnet-grey-600 mb-1">Website</div>
                <div className="text-lg font-semibold text-eucnet-navy">
                  <a
                    href={organization.website.startsWith('http') ? organization.website : `https://${organization.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-eucnet-blue hover:underline"
                  >
                    {organization.website}
                  </a>
                </div>
              </div>
            )}
            {organization.phone && (
              <div>
                <div className="text-sm text-eucnet-grey-600 mb-1">Phone</div>
                <div className="text-lg font-semibold text-eucnet-navy">{organization.phone}</div>
              </div>
            )}
            {(organization.address || organization.city) && (
              <div className="col-span-2">
                <div className="text-sm text-eucnet-grey-600 mb-1">Address</div>
                <div className="text-lg font-semibold text-eucnet-navy">
                  {[organization.address, organization.city, organization.country].filter(Boolean).join(', ')}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Organization Admins - same pattern as OES Detail */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-eucnet-navy">Organization Admins</h2>
            {canManageAdmins && (
              <button
                onClick={() => {
                  fetchAvailableAdmins();
                  setShowAddAdminModal(true);
                }}
                className="bg-eucnet-blue hover:bg-eucnet-navy text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                + Add Admin
              </button>
            )}
          </div>

          {admins.length === 0 ? (
            <p className="text-eucnet-grey-600 text-center py-8">No admins assigned yet</p>
          ) : (
            <div className="space-y-2">
              {admins.map((admin) => (
                <div key={admin.id} className="flex items-center justify-between p-4 bg-eucnet-grey-50 rounded-lg">
                  <div>
                    <div className="font-medium text-eucnet-navy">{admin.username}</div>
                    {admin.email && <div className="text-sm text-eucnet-grey-600">{admin.email}</div>}
                  </div>
                  {isGlobalAdmin && (
                    <button
                      onClick={() => handleRemoveAdmin(admin)}
                      className="text-red-600 hover:text-red-800 p-2"
                      title="Remove from organization"
                    >
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
                        <path d="M8 8l8 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                      </svg>
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add Admin Modal */}
      {showAddAdminModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold text-eucnet-navy mb-4">Add Admin to Organization</h3>
            <form onSubmit={handleAddAdmin}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Select Organization Admin
                </label>
                <CustomSelect
                  value={selectedUserId}
                  onChange={(e) => setSelectedUserId(e.target.value)}
                  options={[
                    { value: '', label: 'Select a user' },
                    ...allUsers.map((u) => ({
                      value: String(u.id),
                      label: `${u.user_info?.first_name || ''} ${u.user_info?.last_name || ''} (${u.email})`.trim() || u.username,
                    })),
                  ]}
                  placeholder="Select a user"
                />
              </div>
              <div className="flex gap-3 justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddAdminModal(false);
                    setSelectedUserId('');
                  }}
                  className="px-4 py-2 border border-eucnet-grey-300 text-eucnet-grey-700 rounded-lg hover:bg-eucnet-grey-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!selectedUserId}
                  className="px-4 py-2 bg-eucnet-blue text-white rounded-lg hover:bg-eucnet-navy disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Add Admin
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmModal
        open={confirmOpen}
        title="Remove admin from organization?"
        message={adminToRemove ? `Are you sure you want to remove "${adminToRemove.username}" (${adminToRemove.email}) from this organization?` : 'Are you sure you want to remove this admin?'}
        confirmText="Remove"
        cancelText="Cancel"
        onConfirm={confirmRemoveAdmin}
        onCancel={() => {
          setConfirmOpen(false);
          setAdminToRemove(null);
        }}
      />
    </Layout>
  );
}
